import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import snakecaseKeys from 'snakecase-keys';

import { Modal, Alert } from 'react-bootstrap-5';

import Button from 'shared/button';
import SelectContacts from './select_contacts';
import ContactsImportResults from './contacts_import_results';
import LoadingSpinner from 'components/loading_spinner';

import { clickAsLink } from 'helpers/link_helpers';

export default class ImportContacts extends React.Component {
  static propTypes = {
    contacts: PropTypes.arrayOf(PropTypes.shape({
      emailAddresses: PropTypes.arrayOf(PropTypes.shape({
        email: PropTypes.string.isRequired
      })).isRequired,
      existsInDatabase: PropTypes.bool,
      id: PropTypes.string,
      nylasUserExists: PropTypes.bool
    })).isRequired,
    contactsIndexPath: PropTypes.string.isRequired,
    duplicateEmails: PropTypes.array.isRequired,
    editUserPath: PropTypes.string,
    handleImportPath: PropTypes.string.isRequired,
    isSameUser: PropTypes.bool,
    profileSettingsPath: PropTypes.string.isRequired,
    user: PropTypes.object
  };

  static steps = {
    stepSelectContacts: 1,
    stepImportResults: 2
  };

  constructor(props) {
    super(props);
    const steps = ImportContacts.steps;
    let selectedContacts = this.props.contacts.map((contact) => {
      if (contact.emailAddresses.length === 0) return;

      if (props.duplicateEmails.indexOf(contact.emailAddresses[0].email) === -1 && !contact.existsInDatabase) {
        return contact;
      }
    });

    selectedContacts =  selectedContacts.filter(Boolean);

    this.props.contacts.sort((contact1, contact2) => {
      const aHasADuplicate = this.props.duplicateEmails.indexOf(contact1.email) > -1;
      if (contact1.nylasUserExists && contact2.nylasUserExists) {
        return 0;
      } else if (contact1.nylasUserExists) {
        return 1;
      } else if (contact2.nylasUserExists || aHasADuplicate || contact1.existsInDatabase) {
        return -1;
      } else return 1;
    });

    this.state = {
      contacts: this.props.contacts,
      selectedContacts: selectedContacts.filter(Boolean),
      step: steps.stepSelectContacts,
      showImportModal: false,
      importingContacts: false,
      importResults: []
    };
  }

  findSelectedContact = (id) => {
    return this.props.contacts.find(contact => contact.id === id);
  };

  handleCancelImport = (e) => {
    const { isSameUser, profileSettingsPath, editUserPath } = this.props;
    const cancelRoute = isSameUser ? profileSettingsPath : editUserPath;
    clickAsLink(window, cancelRoute, e);
  };

  handleDismiss = () => this.setState({ alertMessage: "" });

  handleImport = () => {
    const { duplicateEmails, contacts, handleImportPath, user } = this.props;
    const { selectedContacts } = this.state;

    this.setState({
      importingContacts: true,
      showImportModal: false
    }, () => {
      axios.post(handleImportPath, {
        import: {
          user_id: user.id,
          selected_contacts: snakecaseKeys(selectedContacts),
          contacts: snakecaseKeys(contacts),
          duplicate_emails: duplicateEmails
        }
      }).then((response) => {
        const steps = ImportContacts.steps;

        if (response.data.nextUrl) {
          window.location = response.data.nextUrl;
        }

        this.setState({
          importResults: response.data.contacts,
          step: steps.stepImportResults,
          importingContacts: false,
          alertMessage: "Contacts were successfully imported!"
        });
      });
    });
  };

  handleContactTypeChange = (e) => {
    const { selectedContacts, contacts } = this.state;

    const contactType = e.target.value;
    const contact = this.findSelectedContact(e.target.id);
    const selectedIndex = selectedContacts.findIndex(selectedContact => selectedContact.id === contact.id);
    const contactIndex = contacts.findIndex(contact => contact.id === contact.id);

    if (selectedIndex >= 0) {
      selectedContacts[selectedIndex].type = contactType;
    }

    contacts[contactIndex].type = contactType;

    this.setState({
      selectedContacts: selectedContacts,
      contacts: contacts
    });
  };

  onHideImportModal = () => this.setState({ showImportModal: false });
  onShowImportModal = () => this.setState({ showImportModal: true });

  onSelectContact = (e) => {
    const id = e.target.value;
    const contact = this.findSelectedContact(id);
    const { selectedContacts } = this.state;
    const { duplicateEmails } = this.props;

    let index = selectedContacts.findIndex(selectedContact => selectedContact.id === id);
    if (index >= 0) {
      selectedContacts.splice(index, 1);
    } else  {
      if (duplicateEmails.indexOf(contact.email) >= 0) {
        index = selectedContacts.findIndex(selectedContact => selectedContact.email === contact.email);
        if (index >= 0) {
          selectedContacts.splice(index, 1);
        }
      }
      selectedContacts.push(contact);
    }
    this.setState({ selectedContacts: selectedContacts });
  };

  onSelectAll = (e) => {
    const checked = e.target.checked;
    const { contacts } = this.state;
    const { duplicateEmails } = this.props;
    let selectedContacts = [];

    if (checked) {
      selectedContacts = contacts.map((contact) => {
        if (duplicateEmails.indexOf(contact.email) === -1 && !contact.nylasUserExists) {
          return contact;
        }
      });
    }
    this.setState({ selectedContacts: selectedContacts.filter(Boolean) });
  };

  render() {
    const { importingContacts, alertMessage } = this.state;

    if (importingContacts) {
      return (<LoadingSpinner />);
    } else {
      return (
        <React.Fragment>
          { alertMessage &&
            <Alert dismissible variant="success" onClose={this.handleDismiss}>
              <span className="ms-4">{alertMessage}</span>
            </Alert>
          }
          {this.renderImportTable()}
          {this.renderImportModal()}
        </React.Fragment>
      );
    }
  }

  renderImportTable() {
    const { contacts, step, selectedContacts, importResults } = this.state;
    const { contactsIndexPath, profileSettingsPath, duplicateEmails, editUserPath, isSameUser } = this.props;
    const steps = ImportContacts.steps;

    switch (step) {
    case steps.stepSelectContacts: {
      return (
        <SelectContacts
          contacts={contacts}
          duplicateEmails={duplicateEmails}
          handleCancelImport={this.handleCancelImport}
          handleContactTypeChange={this.handleContactTypeChange}
          handleImport={this.onShowImportModal}
          selectedContacts={selectedContacts}
          onSelectAll={this.onSelectAll}
          onSelectContact={this.onSelectContact}
        />
      );
    }

    case steps.stepImportResults: {
      return (
        <ContactsImportResults
          contacts={importResults}
          contactsIndexPath={contactsIndexPath}
          editUserPath={editUserPath}
          isSameUser={isSameUser}
          profileSettingsPath={profileSettingsPath}
        />
      );
    }
    default:
      return (
        <span>Invalid Step Defined</span>
      );
    }
  }

  renderImportModal() {
    const { showImportModal } = this.state;
    return (
      <Modal show={showImportModal} onHide={this.onHideImportModal}>
        <Modal.Header closeButton>
          <Modal.Title>Import Contacts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to import your contacts?
          <strong> This action cannot be undone.</strong>
        </Modal.Body>
        <Modal.Footer className="text-start">
          <Button.Cancel label="Cancel" onClick={this.onHideImportModal} />
          <Button.Primary label="Import Contacts" onClick={this.handleImport} />
        </Modal.Footer>
      </Modal>
    );
  }
}
